import React, {CSSProperties, KeyboardEvent, ReactNode, createContext} from 'react'

import {TabVariant, tabSizes} from './styles.js'

/** What onChange (and a Tab's onClick) gets: the value of the clicked tab, at the place callers read it from */
export interface TabChangeEvent {
    target: {value: string}
}

interface TabsContextValue {
    value?: string
    values?: string[]
    onChange?: (event: TabChangeEvent) => void
    variant?: TabVariant
}

/** Lets every Tab inside Tabs know the selected value, also when it is wrapped in another element (a ToolTip) */
export const TabsContext = createContext<TabsContextValue | null>(null)

interface TabsProps {
    style?: CSSProperties
    children?: ReactNode
    /** Value of the active Tab */
    value?: string
    /** Values of the active Tabs, when more than one can be active */
    values?: string[]
    onChange?: (event: TabChangeEvent) => void
    /** Look of every Tab in the row */
    variant?: TabVariant
}

// The tabs of this list (not of a list inside one of its tabs) that can be selected
function getTabs(list: HTMLElement) {
    return Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]')).filter((tab) => {
        return tab.closest('[role="tablist"]') === list && tab.getAttribute('aria-disabled') !== 'true'
    })
}

/**
 * A row of Tabs. The Tab whose value is value (or in values) is active, clicking a Tab calls onChange with its value.
 * Arrow keys, Home and End move the focus between the tabs, Enter or Space selects the focused one.
 */
export default function Tabs({style, children, value, values, onChange, variant}: TabsProps) {
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            return
        }

        const tabs = getTabs(event.currentTarget)
        const index = tabs.indexOf(event.target as HTMLElement)

        if (index === -1) {
            return
        }

        event.preventDefault()

        const next = event.key === 'Home' ?
            0 :
            event.key === 'End' ?
                tabs.length - 1 :
                (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length

        tabs[next].focus()
    }

    const defaultStyle: CSSProperties = {
        display: 'flex',
        height: tabSizes.height,
        ...style
    }

    return (
        <TabsContext.Provider value={{value, values, onChange, variant}}>
            <div role='tablist' style={defaultStyle} onKeyDown={onKeyDown}>
                {children}
            </div>
        </TabsContext.Provider>
    )
}
